// scripts/publish/md-render.mjs — SB-015 零依赖 markdown 渲染器（GFM 子集）。
//
// 结论（publish-pipeline.md §3）：不引入 markdown-it。data_content 语料经核查只使用
// 一个封闭子集：ATX 标题 / 段落 / 引用块 / 有序无序列表（嵌套）/ GFM 表格 / 围栏代码 /
// 行内 code、**strong**、*em*、~~del~~、链接、图片、hr；无内嵌 HTML、无脚注、无 setext。
// 本仓库脚本链现状为零 npm 依赖（deploy/dictionary 同例），为此引入 package.json +
// node_modules 安装链不划算。若日后内容超出子集（内嵌 HTML/脚注等），按设计文档升级条款
// 切换 markdown-it@14.1.0（钉版）——渲染入口签名保持不变。
//
// 安全：一切原文按 HTML 转义输出（含代码块与属性），不透传任何原始 HTML。
// node >= 22，零外部依赖。
import { entryIdOf } from './entry-id.mjs';

// ── front-matter ────────────────────────────────────────────────────────────
// 形如文件头部 `---\nkey: value\n---\n` 的简单键值块（A8：源语言等元数据）。
// 返回 { meta: Object|null, body: string }；无 front-matter 时 meta 为 null。
export function extractFrontMatter(text) {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(text);
  if (!m) return { meta: null, body: text };
  const meta = {};
  for (const line of m[1].split(/\r?\n/)) {
    const kv = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(line);
    if (kv) meta[kv[1]] = kv[2].trim();
  }
  return { meta, body: text.slice(m[0].length) };
}

// ── HTML 转义 ───────────────────────────────────────────────────────────────
const esc = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// ── 链接协议白名单（第八十八批）─────────────────────────────────────────────
// 到本批为止，显式链接 `[标签](目标)` 的目标是**原样**写进 href 的（只做 HTML 转义，
// 不看协议）。转义挡住了属性逃逸，挡不住 `javascript:`：
//     [点我](javascript:alert(document.cookie))  →  <a href="javascript:alert(...)">
// 那是一枚点一下就执行的脚本。此前不出事只因为**渲染的全是我方 git 里的文档**——
// 作者可信、内容经评审、且渲染发生在构建期。本批起同一只渲染器要渲染
// **用户当场提交的 ticket / 工作项正文**，这条前提当场消失。
//
// 判据是**允许集**而不是黑名单（与 slug 校验、仓单同一条纪律）：黑名单永远少列一种
// 写法，而漏掉一种的后果是一枚可执行的链接。
//   · 无协议（`/docs/x`、`./a.md`、`#锚点`、`a/b.png`）⇒ 放行，站内相对链接全靠它；
//   · http / https / mailto ⇒ 放行；
//   · 其余一切带协议的（javascript / data / vbscript / file / 自定义 scheme）⇒ 拒。
// 拒了之后**不留一个坏链接**，而是把标签当普通文字渲染——一个 href="#" 的假链接
// 会让人以为点了没反应是页面卡了。
const SAFE_SCHEMES = new Set(['http', 'https', 'mailto']);
export function safeHref(target) {
  // 控制字符与空白先剥掉再判协议：浏览器解析 URL 时会忽略它们，于是
  // `java\tscript:alert(1)` 在浏览器眼里就是 `javascript:`，而在朴素的正则眼里不是。
  const cleaned = [...String(target ?? '')].filter((ch) => ch.charCodeAt(0) > 32 && ch.charCodeAt(0) !== 127).join('');
  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(cleaned);
  if (!m) return target;                                   // 无协议 = 相对/锚点，放行
  return SAFE_SCHEMES.has(m[1].toLowerCase()) ? target : null;
}

// 标题锚点 slug（保留 CJK；重复时 -2/-3 …）
// 第一百八十批起导出：断锚判据（scripts/lint/md-render-check.mjs）要按**同一条**规则
// 算目标页有哪些锚——判据自己再写一遍 slug 规则，两边迟早各改一处。
export function slugify(text, used) {
  let s = text.toLowerCase().trim()
    .replace(/[^\w一-鿿㐀-䶿 -]/g, '')
    .replace(/\s+/g, '-') || 'section';
  if (used.has(s)) { let i = 2; while (used.has(`${s}-${i}`)) i++; s = `${s}-${i}`; }
  used.add(s);
  return s;
}

// ── 行内解析 ────────────────────────────────────────────────────────────────
// ctx.rewriteLink({ href, isImage }) -> { href, className? }；未提供则原样。
function renderInline(src, ctx) {
  let out = '';
  let i = 0;
  const n = src.length;
  const boundary = (c) => c === undefined || !/[A-Za-z0-9一-鿿]/.test(c);

  const findRun = (ch, len, from) => {
    // 定位下一个等长定界符 run（长度恰为 len，且非空内容）
    for (let k = from; k < n; k++) {
      if (src[k] === '\\') { k++; continue; }
      if (src[k] === ch) {
        let j = k;
        while (j < n && src[j] === ch) j++;
        if (j - k === len && k > from) return k;
        k = j - 1;
      }
    }
    return -1;
  };

  while (i < n) {
    const c = src[i];

    // 反斜杠转义
    if (c === '\\' && i + 1 < n && /[\\`*_{}[\]()#+\-.!~|]/.test(src[i + 1])) {
      out += esc(src[i + 1]); i += 2; continue;
    }

    // 行内代码：`...`（支持多反引号定界）
    if (c === '`') {
      let open = i;
      while (open < n && src[open] === '`') open++;
      const ticks = src.slice(i, open);
      const close = src.indexOf(ticks, open);
      if (close !== -1) {
        out += `<code>${esc(src.slice(open, close).trim())}</code>`;
        i = close + ticks.length; continue;
      }
    }

    // 自动链接：<https://…> / <mailto:…>（CommonMark autolink）
    //
    // 第五十九批补。此前**没有这个分支**，于是 `<https://pin.it/x>` 一路掉进转义路径
    // 变成 `&lt;https://pin.it/x&gt;`——一段带尖括号、不可点的纯文本。实测全库 112 处，
    // 全部集中在风格参考图版与美术图集页的「来源」列：也就是说，我们要求外包"看得到
    // 每张参考图的出处"的那一整列，从第五十一批起就是死的。
    //
    // 没人报，因为没人点。这与本批的音频、上批的 artlist 是同一族：**一个页面宣称了
    // 一种它并不具备的能力**，而缺陷不产生任何错误信号。
    //
    // 只认带 scheme 的绝对 URL 与 mailto：CommonMark 的 autolink 要求 scheme，
    // 而放宽到"看着像域名就链接"会把正文里的 `<div>`、`<T>` 之类一并咬掉。
    if (c === '<') {
      const gt = src.indexOf('>', i + 1);
      if (gt !== -1) {
        const inner = src.slice(i + 1, gt);
        if (/^(?:https?|mailto):[^\s<>]+$/i.test(inner)) {
          const href = esc(inner);
          out += `<a href="${href}">${esc(inner)}</a>`;
          i = gt + 1; continue;
        }
      }
    }

    // 图片 / 链接
    if ((c === '!' && src[i + 1] === '[') || c === '[') {
      const isImage = c === '!';
      const lb = isImage ? i + 1 : i;
      let depth = 0, rb = -1;
      for (let k = lb; k < n; k++) {
        if (src[k] === '\\') { k++; continue; }
        if (src[k] === '[') depth++;
        else if (src[k] === ']') { depth--; if (depth === 0) { rb = k; break; } }
      }
      if (rb !== -1 && src[rb + 1] === '(') {
        let pd = 0, rp = -1;
        for (let k = rb + 1; k < n; k++) {
          if (src[k] === '\\') { k++; continue; }
          if (src[k] === '(') pd++;
          else if (src[k] === ')') { pd--; if (pd === 0) { rp = k; break; } }
        }
        if (rp !== -1) {
          const label = src.slice(lb + 1, rb);
          let target = src.slice(rb + 2, rp).trim();
          // 可选 title："..."
          const tm = /^(\S+)\s+"([^"]*)"$/.exec(target);
          let title = '';
          if (tm) { target = tm[1]; title = tm[2]; }
          const rewritten = ctx.rewriteLink
            ? ctx.rewriteLink({ href: target, isImage })
            : { href: target };
          // 第八十八批：协议白名单（见 safeHref 的头注）。判在 rewriteLink **之后**——
          // 重写钩子自己也会产出 href，而"我方代码产出的一定安全"正是这类漏洞的
          // 标准前提。拒了就把标签当普通文字渲染：不留一个点了没反应的假链接。
          // ── 图片开关（第一百零九批处置轮 R2）────────────────────────────
          // `ctx.images === false` ⇒ 图片语法整个降级成普通文字。协议白名单挡不住
          // 这一手：`![](/api/secret-deliveries/123)` 是**相对 URL**，白名单按设计
          // 放行它（站内相对链接全靠这一条）。而图片与链接的差别正是这里的全部要害
          // ——链接要人点，图片是**打开页面就自动发出的一次 GET，带着读者自己的
          // cookie**。本仓有两条会改状态的 GET（密递取件即焚、站内信点开即已读），
          // 于是一段攻击者可控的正文就能在受害者的浏览器里替他焚掉密件、替他把
          // 整个收件箱标成已读。端点侧另有 `Sec-Fetch-Dest` 那道闸（route_util），
          // 两道各自独立：这一道管"别让它被渲染出来"，那一道管"就算被渲染出来也
          // 别照做"，而单靠任何一道都要求下一个新增面的人记得同一件事。
          const raw = rewritten.href ?? target;
          if (isImage && ctx.images === false) {
            out += esc(label);
            i = rp + 1; continue;
          }
          const safe = safeHref(raw);
          if (safe === null) {
            out += isImage ? esc(label) : renderInline(label, ctx);
            i = rp + 1; continue;
          }
          const href = esc(safe);
          const cls = rewritten.className ? ` class="${esc(rewritten.className)}"` : '';
          const ttl = title ? ` title="${esc(title)}"` : '';
          if (isImage) {
            out += `<img src="${href}" alt="${esc(label)}"${ttl}${cls}>`;
          } else {
            out += `<a href="${href}"${ttl}${cls}>${renderInline(label, ctx)}</a>`;
          }
          i = rp + 1; continue;
        }
      }
    }

    // strong：** / __
    if ((c === '*' || c === '_') && src[i + 1] === c) {
      const close = findRun(c, 2, i + 2);
      if (close !== -1 && !/\s/.test(src[i + 2])) {
        out += `<strong>${renderInline(src.slice(i + 2, close), ctx)}</strong>`;
        i = close + 2; continue;
      }
    }

    // em：* / _（_ 要求词边界，避免 file_name.test.ts 误判）
    if (c === '*' || c === '_') {
      const okOpen = c === '*' || boundary(src[i - 1]);
      const close = okOpen && !/\s/.test(src[i + 1] ?? ' ') ? findRun(c, 1, i + 1) : -1;
      const okClose = close !== -1 && !/\s/.test(src[close - 1]) &&
                      (c === '*' || boundary(src[close + 1]));
      if (okClose) {
        out += `<em>${renderInline(src.slice(i + 1, close), ctx)}</em>`;
        i = close + 1; continue;
      }
    }

    // del：~~
    if (c === '~' && src[i + 1] === '~') {
      const close = src.indexOf('~~', i + 2);
      if (close !== -1) {
        out += `<del>${renderInline(src.slice(i + 2, close), ctx)}</del>`;
        i = close + 2; continue;
      }
    }

    out += esc(c);
    i++;
  }
  return out;
}

// ── 块级解析 ────────────────────────────────────────────────────────────────
const RE_HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const RE_FENCE   = /^```(.*)$/;
const RE_HR      = /^(?:\*{3,}|-{3,}|_{3,})\s*$/;
const RE_QUOTE   = /^>\s?(.*)$/;
const RE_LIST    = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const RE_TBLSEP  = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function splitTableRow(line) {
  const cells = [];
  let cur = '', depth = 0;
  const s = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '\\' && s[i + 1] === '|') { cur += '|'; i++; continue; }
    if (c === '`') depth ^= 1;
    if (c === '|' && !depth) { cells.push(cur.trim()); cur = ''; continue; }
    cur += c;
  }
  cells.push(cur.trim());
  return cells;
}

function renderBlocks(lines, ctx) {
  let html = '';
  let i = 0;
  const n = lines.length;

  const isSpecial = (l) =>
    RE_HEADING.test(l) || RE_FENCE.test(l) || RE_HR.test(l) ||
    RE_QUOTE.test(l) || RE_LIST.test(l);

  while (i < n) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // 围栏代码
    const fence = RE_FENCE.exec(line);
    if (fence) {
      const lang = fence[1].trim();
      const buf = [];
      i++;
      while (i < n && !/^```\s*$/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;  // 吃掉闭合围栏
      const cls = lang ? ` class="language-${esc(lang)}"` : '';
      html += `<pre><code${cls}>${esc(buf.join('\n'))}\n</code></pre>\n`;
      continue;
    }

    // 标题
    const h = RE_HEADING.exec(line);
    if (h) {
      const level = h[1].length;
      const raw = h[2];
      // 第八十八批：`headingIds:false` 时不出 id。**用户提交的正文必须走这条**——
      // `usedSlugs` 是 per-call 的，而页面上会同时挂着好几段正文（工作项页每个折叠
      // 面板都保持挂载，工单页原文与译文并排），两段都写「## 背景」就产出两个
      // id="背景"。重复 id 让页内锚点指向哪一个由文档顺序决定，是那种"看起来能用、
      // 偶尔跳错地方"的缺陷。发布管线仍要 id（目录与跨文档锚点靠它），故默认为真。
      const id = ctx.headingIds ? slugify(raw.replace(/[`*_~[\]!]/g, ''), ctx.usedSlugs) : null;
      if (level === 1 && ctx.title === null) ctx.title = raw.replace(/[`*_~]/g, '');
      html += id === null
        ? `<h${level}>${renderInline(raw, ctx)}</h${level}>
`
        : `<h${level} id="${id}">${renderInline(raw, ctx)}</h${level}>
`;
      i++; continue;
    }

    // hr（front-matter 已剥离；紧邻段落的 '---' 也按 hr 处理——语料无 setext 标题）
    if (RE_HR.test(line)) { html += '<hr>\n'; i++; continue; }

    // 引用块（连续 > 行，内部递归）
    if (RE_QUOTE.test(line)) {
      const buf = [];
      while (i < n && RE_QUOTE.test(lines[i])) { buf.push(RE_QUOTE.exec(lines[i])[1]); i++; }
      html += `<blockquote>\n${renderBlocks(buf, ctx)}</blockquote>\n`;
      continue;
    }

    // 表格：本行含 | 且下一行是分隔行
    if (line.includes('|') && i + 1 < n && RE_TBLSEP.test(lines[i + 1]) &&
        lines[i + 1].includes('|')) {
      const header = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map((c) => {
        const l = c.startsWith(':'), r = c.endsWith(':');
        return l && r ? 'center' : r ? 'right' : l ? 'left' : '';
      });
      const attr = (k) => (aligns[k] ? ` style="text-align:${aligns[k]}"` : '');
      i += 2;
      let thead = '<tr>';
      header.forEach((c, k) => { thead += `<th${attr(k)}>${renderInline(c, ctx)}</th>`; });
      thead += '</tr>';
      let tbody = '';
      while (i < n && lines[i].includes('|') && lines[i].trim()) {
        const row = splitTableRow(lines[i]);
        tbody += '<tr>';
        header.forEach((_, k) => {
          tbody += `<td${attr(k)}>${renderInline(row[k] ?? '', ctx)}</td>`;
        });
        tbody += '</tr>\n';
        i++;
      }
      html += `<table>\n<thead>${thead}</thead>\n<tbody>\n${tbody}</tbody>\n</table>\n`;
      continue;
    }

    // 列表（按缩进嵌套；连续列表行 + 缩进续行为一个块）
    //
    // 松散列表（第八十批）：项间空行**不再**终止列表块。此前一遇空行即收尾并输出
    // `</ol>`，下一项另起一个新 `<ol>`——叠加下面 renderList 不写 start=，读者看到的
    // 是 1./1./1./1. 而不是 1./2./3./4.（全仓 7 处，含论点库与两章规格）。判据是
    // **前瞻**：空行之后若仍是列表内容（列表行或缩进块，含项间的缩进表格），空行归入
    // 本块继续；否则照旧终止——空行后接顶格段落仍然正确地结束列表。
    if (RE_LIST.test(line)) {
      const head = RE_LIST.exec(line);
      const blockIndent = head[1].length;
      const blockOrdered = /^\d/.test(head[2]);
      // 跨空行续接的门槛比同块内更严：必须是**同层同型**的列表项，或更深的缩进块。
      // 否则「有序列表 → 空行 → 无序列表」这两个相邻但不同的列表会被并成一个
      // <ol>，无序项被当成有序项编号（本批实测 auth-flow 的 5 项变 8 项）。
      const continuesBlock = (l) => {
        if (/^\s{2,}/.test(l) && /^\s*\S/.test(l) && l.search(/\S/) >= blockIndent + 2) return true;
        const m = RE_LIST.exec(l);
        if (!m) return false;
        if (m[1].length > blockIndent) return true;                 // 嵌套子列表
        return m[1].length === blockIndent && /^\d/.test(m[2]) === blockOrdered;
      };
      const buf = [];
      while (i < n) {
        const cur = lines[i];
        if (RE_LIST.test(cur) || (cur.trim() && /^\s{2,}/.test(cur) && buf.length)) {
          buf.push(cur); i++; continue;
        }
        if (!cur.trim() && buf.length) {
          let j = i;
          while (j < n && !lines[j].trim()) j++;
          if (j < n && continuesBlock(lines[j])) {
            for (; i < j; i++) buf.push(lines[i]);
            continue;
          }
        }
        break;
      }
      html += renderList(buf, ctx);
      continue;
    }

    // 段落
    const buf = [line];
    i++;
    while (i < n && lines[i].trim() && !isSpecial(lines[i]) &&
           !(lines[i].includes('|') && i + 1 < n && RE_TBLSEP.test(lines[i + 1]))) {
      buf.push(lines[i]); i++;
    }
    const text = buf.map((l) => l.replace(/\s{2,}$/, '<!--br-->')).join('\n');
    html += `<p>${renderInline(text, ctx).replace(/&lt;!--br--&gt;/g, '')
                   .replace(/<!--br-->/g, '<br>')}</p>\n`;
  }
  return html;
}

function renderList(lines, ctx) {
  // 解析首个 marker 的缩进为本层缩进；更深缩进的行归入当前 item。
  const first = RE_LIST.exec(lines[0]);
  const indent = first[1].length;
  const ordered = /^\d/.test(first[2]);
  const items = [];   // 每项：{ text, childLines: [] }

  // ── 第八十七批：**同段续行归本段，不归子块** ──────────────────────────────
  // 病灶：此前 item.text 只取 marker 那一行（m[3]），**其余续行一律进 childLines**，
  // 而 childLines 走 renderBlocks → 被包成 `<p>`。后果是任何**跨软换行的行内构造**
  // 在列表项里一律失效——加粗、斜体、行内代码、链接、删除线，全都断在换行处：
  //
  //     - **一段加粗跨了
  //       两行。** 后续文字
  //   渲染成 →  <li>**一段加粗跨了<p>两行。** 后续文字</p></li>
  //   `**` 原样漏出来，续行还多出一个 <p>。
  //
  // 而**段落里同样的写法是好的**——段落那条路径先把行 join('\n') 再交给
  // renderInline（见 renderBlocks 的段落分支）。同一份文档里，一条加粗写得下一行
  // 就正常、写不下就坏掉，作者无从察觉：源码看起来完全一样。
  //
  // 正确语义（CommonMark）：列表项内的续行**默认属于该项的第一个段落**（惰性续行）；
  // 只有遇到空行、或遇到本身就是块级构造的行（嵌套列表/围栏/标题/引用/分隔线/表格）
  // 才开始一个新的块。本函数照此切分：先收"同段续行"并入 text，其余仍走 childLines。
  //
  // 这一条修的是**一整类**问题而不是加粗一种：所有行内构造都走同一个 renderInline，
  // 把它们拿到的字符串修对，五种构造一起好。
  // 判据要作用在**子块渲染器将会看到的那份字符串**上，即剥掉本层缩进之后的行。
  // 直接拿原始行去测是错的：RE_FENCE 是 /^```/ 锚在行首的，而项内的围栏必然带缩进
  // （`  ```），于是原始行永远匹配不上——围栏会被当成同段续行并入 text，最后被
  // renderInline 读成**行内代码**。这一条是本次改动自己的回归用例咬出来的
  // （「项内围栏代码块仍是子块」当场转红），故连同判据一起钉在这里。
  const isBlockStart = (l) => {
    const stripped = l.slice(indent + 2);
    if (!stripped.trim()) return true;        // 空行：同段到此为止
    return RE_HEADING.test(stripped) || RE_FENCE.test(stripped) || RE_HR.test(stripped) ||
           RE_QUOTE.test(stripped) || RE_LIST.test(l);   // 列表用原始行（缩进即层级）
  };

  for (let i = 0; i < lines.length; i++) {
    const m = RE_LIST.exec(lines[i]);
    if (m && m[1].length <= indent) {
      const it = { text: m[3], childLines: [] };
      // 紧随其后的续行：只要不是块级开头，就是同一段的折行，并入 text。
      // **只吃紧邻的那一串**——一旦遇到空行或块级构造就停，后面的仍归 childLines，
      // 于是"项内还有第二段/嵌套列表/代码块"这些情形一个都不受影响。
      let j = i + 1;
      while (j < lines.length) {
        const stripped = lines[j].slice(indent + 2);
        if (isBlockStart(lines[j])) break;
        // 下一个同层 marker 也要停（isBlockStart 已含 RE_LIST，这里是显式提醒）。
        it.text += '\n' + stripped.replace(/^\s+/, '');
        j++;
      }
      items.push(it);
      i = j - 1;
    } else if (items.length) {
      // 子行：剥掉本层缩进 + marker 宽度（近似 2）
      items[items.length - 1].childLines.push(lines[i].slice(indent + 2));
    }
  }
  const tag = ordered ? 'ol' : 'ul';
  // 起始号（第八十批）：此前 marker 的数值被整个丢弃，`<ol>` 一律从 1 数起——作者
  // 刻意接着上一节续编号（正典 §9 的「准则 4」）时，正文写"准则 4"而页面显示"1."。
  // CommonMark 的语义是首个 marker 定 start，其余由浏览器顺推；照此输出。
  const startNum = ordered ? Number.parseInt(first[2], 10) : NaN;
  let html = Number.isFinite(startNum) && startNum !== 1
    ? `<ol start="${startNum}">\n` : `<${tag}>\n`;
  for (const it of items) {
    // ── 条目锚（第一百八十批，opt-in）──────────────────────────────────────
    // 规格册的条目是 `- **FR-P4 …**：` 这种列表项，而跨页引用此前只能写「见 FR-P4」
    // 或裸「§N」——读者得自己翻。发布态给这类 `<li>` 发 `id="FR-P4"`，链接就能写
    // `functional-requirements.md#FR-P4`。判据与 data-content-parity 同一条正则
    // （entry-id.mjs），齐平判据数到的条目，页面上必有同名锚。
    // **默认关**：工单正文那只调用点不该发 id（同屏多段，与 headingIds:false 同一理由）。
    // cpp 移植（md_render.cpp render_list，`entry_ids` 旗标）已于第二百六十九批追平本分支，
    // 向量组含 entryIds:true 的一组用例——这里改判据/改产出的字节，那边必须同步。
    // 去重集与标题 slug 共用：一个 id 在页内只能出一次，撞了就**不发**并交给 warn 钩子
    // ——静默发第二个 id 的后果是链接跳到文档顺序里先出现的那个，"看起来能用、偶尔跳错"。
    let liAttr = '';
    if (ctx.entryIds === true) {
      const id = entryIdOf(it.text);
      if (id !== null) {
        if (ctx.usedSlugs.has(id)) ctx.onDuplicateId?.(id);
        else { ctx.usedSlugs.add(id); liAttr = ` id="${esc(id)}"`; }
      }
    }
    html += `<li${liAttr}>${renderInline(it.text, ctx)}`;
    if (it.childLines.length) {
      const childBlocks = renderBlocks(it.childLines, ctx);
      html += `\n${childBlocks}`;
    }
    html += '</li>\n';
  }
  html += `</${tag}>\n`;
  return html;
}

// ── 入口 ────────────────────────────────────────────────────────────────────
// renderMarkdown(body, { rewriteLink }) -> { html, title }
// body 应为已剥 front-matter 的正文；title = 第一个 H1（去行内标记），无则 null。
// HTML 注释被**丢弃**而非转义。转义（此前的行为）会把 `<!-- … -->` 当成正文打印
// 给读者看——作者写给作者的备注被原样发布了出去。丢弃不产出任何内容，故并不削弱
// "不透传任何原始 HTML"的承诺：没有透传，只是少了噪音。（SB-033 审计：生成视图的
// GENERATED VIEW 标记注释正被渲染成正文。）
export function stripHtmlComments(body) {
  return String(body).replace(/<!--[\s\S]*?-->/g, '');
}

export function renderMarkdown(body, opts = {}) {
  // headingIds 默认真：发布管线的目录与跨文档锚点依赖它。渲染用户提交的正文时
  // 传 false（见上面标题分支的注释）。
  // images 默认真（发布管线的文档里有真图）；渲染**用户当场提交的正文**时传 false，
  // 理由见 renderInline 的图片分支：一张图片是一次**由浏览器自动发起、带着读者
  // cookie 的 GET**，而本仓有会改状态的 GET。
  // entryIds 默认**假**（与上两项方向相反）：第一百八十批新增的行为，只有发布管线
  // （publish.mjs）显式打开。cpp 移植第二百六十九批追平（需求方 2026-09-17 裁：条目锚
  // C++ 侧也要发）：md_render.h 的 `entry_ids` 同样默认 false、`on_duplicate_id` 钩子
  // 对应 onDuplicateId，向量含 entryIds:true 的一组用例。onDuplicateId(id) 在页内撞 id
  // 时被调用一次——发布侧接 warn，判据侧可以接断言。
  const ctx = { rewriteLink: opts.rewriteLink, usedSlugs: new Set(), title: null,
                headingIds: opts.headingIds !== false, images: opts.images !== false,
                entryIds: opts.entryIds === true, onDuplicateId: opts.onDuplicateId };
  const html = renderBlocks(stripHtmlComments(body).split(/\r?\n/), ctx);
  return { html, title: ctx.title };
}
